import React, { useMemo, useState } from "react";
import { Checkbox, Input, Search, Tags } from "../../ui";
import { BRANCH_TAG } from "./applicationsModel";

// Searchable multi-select of a store's branches, shared by every application
// flow (subscribe, integrate, activate, deactivate). Each row shows the branch,
// its integration status for the app and, when it cannot be picked, why.
// The branches that are selected are always listed above the list, even when
// the search hides them, so the user sees exactly what will be submitted.
//
//   branches    [{ id, name, city, phone, integration }]
//   selected    ids
//   reasonFor   (branch) => string; non-empty disables the row
//   renderExtra (branch) => node shown under a selected row (e.g. per-branch fields)
export default function BranchPicker({ branches, selected, onToggle, reasonFor = () => "", renderExtra, originId = null, t, disabled = false }) {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const visible = useMemo(
    () => (needle ? branches.filter((branch) => [branch.name, branch.city, branch.phone].some((value) => String(value || "").toLowerCase().includes(needle))) : branches),
    [branches, needle],
  );
  const chosen = branches.filter((branch) => selected.includes(branch.id));

  return (
    <div className="lud-app-picker">
      <Input iconStart={<Search size={16} />} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t.flow.searchBranches} aria-label={t.flow.searchBranches} />
      {chosen.length > 0 && (
        <div className="lud-app-tags" aria-label={t.flow.selectedBranches}>
          {chosen.map((branch) => (
            <Tags key={branch.id} variant="primary" appearance="soft" size="sm" closable={!disabled} closeLabel={`${t.flow.removeBranch} ${branch.name}`} onClose={() => onToggle(branch.id)}>
              {branch.id === originId ? `${branch.name} · ${t.flow.originBranch}` : branch.name}
            </Tags>
          ))}
        </div>
      )}
      <ul className="lud-app-branch-list">
        {visible.map((branch) => {
          const reason = reasonFor(branch);
          return (
            <li key={branch.id} className={reason ? "is-disabled" : ""}>
              <Checkbox checked={selected.includes(branch.id) && !reason} disabled={Boolean(reason) || disabled} onChange={() => onToggle(branch.id)} aria-label={branch.name} label={<strong>{branch.name || "—"}</strong>} />
              {branch.integration && (
                <Tags variant={BRANCH_TAG[branch.integration] || "neutral"} appearance="soft" size="sm">
                  {t.status[branch.integration] || branch.integration}
                </Tags>
              )}
              {reason && <small className="lud-app-reason">{reason}</small>}
              {renderExtra && selected.includes(branch.id) && !reason && renderExtra(branch)}
            </li>
          );
        })}
        {visible.length === 0 && <li className="lud-app-hint">{t.flow.noMatch}</li>}
      </ul>
    </div>
  );
}
